import React, { useEffect, useRef, useState } from 'react';
import api from '../api/axios';
import { FaBullseye, FaDollarSign, FaFilter } from 'react-icons/fa';
import UiText from '../components/UiText';
import { Loader2 } from 'lucide-react';
import AddDealModal from "../components/AddDealModal";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import useDebounce from "../utils/useDebounce";

// Persist filter/sort/pagination state across navigation (e.g. Deals -> Deal
// Details -> Edit -> Back) so returning to this page keeps the previous view
// instead of resetting to the unfiltered list. Scoped to the tab's session.
const DEALS_FILTERS_STORAGE_KEY = "dealsPage:filters";

const getPersistedDealFilters = () => {
  try {
    const raw = window.sessionStorage.getItem(DEALS_FILTERS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const DealsPage = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(() => getPersistedDealFilters()?.search ?? '');
  const debouncedSearch = useDebounce(search, 300);
  const [activeStage, setActiveStage] = useState(() => getPersistedDealFilters()?.activeStage ?? "all");
  const [companyFilter, setCompanyFilter] = useState(() => getPersistedDealFilters()?.companyFilter ?? "all");
  const [authorFilter, setAuthorFilter] = useState(() => getPersistedDealFilters()?.authorFilter ?? "all");
  const [createdFrom, setCreatedFrom] = useState(() => getPersistedDealFilters()?.createdFrom ?? "");
  const [createdTo, setCreatedTo] = useState(() => getPersistedDealFilters()?.createdTo ?? "");
  const [closeFrom, setCloseFrom] = useState(() => getPersistedDealFilters()?.closeFrom ?? "");
  const [closeTo, setCloseTo] = useState(() => getPersistedDealFilters()?.closeTo ?? "");
  const [minValue, setMinValue] = useState(() => getPersistedDealFilters()?.minValue ?? "");
  const [maxValue, setMaxValue] = useState(() => getPersistedDealFilters()?.maxValue ?? "");
  const [companies, setCompanies] = useState([]);
  const [stages, setStages] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(() => getPersistedDealFilters()?.showAdvanced ?? false);
  const [onlyMyDeals, setOnlyMyDeals] = useState(() => getPersistedDealFilters()?.onlyMyDeals ?? false);
  const [currentPage, setCurrentPage] = useState(() => getPersistedDealFilters()?.currentPage ?? 1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewAuthorFilter = user?.role === 'admin' || user?.role === 'executive';
    // Sorting state
  const [sortConfig, setSortConfig] = useState(() => getPersistedDealFilters()?.sortConfig ?? { field: 'created_at', direction: 'desc' });
  // Skip the very first run of the "reset page on filter change" effect below,
  // so a restored currentPage from sessionStorage isn't immediately reset to 1.
  const isInitialFilterRender = useRef(true);

  const advancedCount = (createdFrom || createdTo || closeFrom || closeTo || minValue || maxValue) ? [createdFrom, createdTo, closeFrom, closeTo, minValue, maxValue].filter(v => v !== "" && v !== null && v !== undefined).length : 0;

  const clearAdvancedFilters = () => {
    setCreatedFrom("");
    setCreatedTo("");
    setCloseFrom("");
    setCloseTo("");
    setMinValue("");
    setMaxValue("");
  };

  const hasActiveFilters = Boolean(
    search || activeStage !== "all" || companyFilter !== "all" || authorFilter !== "all" || onlyMyDeals || advancedCount > 0
  );

  const clearAllFilters = () => {
    setSearch("");
    setActiveStage("all");
    setCompanyFilter("all");
    setAuthorFilter("all");
    setOnlyMyDeals(false);
    clearAdvancedFilters();
  };

  // ✅ NEW: allow Companies page to navigate here with a company filter
  const location = useLocation();

  // ---- Fetch deals from backend ----
  const fetchDeals = async () => {
    try {
      const res = await api.get('/api/deals/', { params: { limit: 5000 } });
      setDeals(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch deals.");
    } finally {
      setLoading(false);
    }
  };

  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchDeals();
  }, []);

  useEffect(() => {
    const loadStages = async () => {
      try {
        const res = await api.get('/api/stages/');
        setStages(res.data || []);
      } catch (err) {
        console.error('Failed to load stages', err);
      }
    };
    loadStages();
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, []);

  // ---- Fetch companies for company names ----
  const fetchCompanies = async () => {
    try {
      const res = await api.get('/api/companies/', { params: { limit: 500 } });
      setCompanies(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch companies.");
    }
  };

  // ✅ NEW: apply company filter if we came from Companies page
  useEffect(() => {
    const incomingCompanyId = location?.state?.companyId;
    if (incomingCompanyId) {
      setCompanyFilter(String(incomingCompanyId));
    }
  }, [location?.state?.companyId]);

  // ---- Filter by stage ----
  const filterByStage = (deal) => {
    if (activeStage === "all") return true;
    return deal.stage.toLowerCase() === activeStage.toLowerCase();
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString();
  };

  const inDateRange = (dateStr, from, to) => {
    if (!from && !to) return true;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return true; // don't block if backend sends non-date
    const fromD = from ? new Date(from + "T00:00:00") : null;
    const toD = to ? new Date(to + "T23:59:59") : null;
    if (fromD && d < fromD) return false;
    if (toD && d > toD) return false;
    return true;
  };

  const inValueRange = (amount, min, max) => {
    const v = parseFloat(amount || 0);
    const minV = min === "" ? null : parseFloat(min);
    const maxV = max === "" ? null : parseFloat(max);
    if (minV !== null && !Number.isNaN(minV) && v < minV) return false;
    if (maxV !== null && !Number.isNaN(maxV) && v > maxV) return false;
    return true;
  };

  const matchesCompany = (deal) => {
    if (companyFilter === "all") return true;
    // compare against company_id primarily
    if (deal.company_id && String(deal.company_id) === String(companyFilter)) return true;
    // fallback by name if needed
    const selected = companies.find(c => String(c.id) === String(companyFilter));
    if (selected && deal.company_name) {
      return String(deal.company_name).toLowerCase() === String(selected.name).toLowerCase();
    }
    return false;
  };

  const matchesAuthor = (deal) => {
    if (!canViewAuthorFilter || authorFilter === "all") return true;
    return String(deal?.created_by || "") === String(authorFilter);
  };

  const authorOptions = Array.from(
    new Map(
      deals
        .filter((deal) => deal?.created_by)
        .map((deal) => [
          String(deal.created_by),
          {
            id: String(deal.created_by),
            name: deal.created_by_name || deal.created_by,
          },
        ])
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  const stageLabel = (stage) => {
    if (!stage) return '—';
    const stageStr = (stage || '').toString().toLowerCase().trim();
    
    // Try to find by key
    const foundByKey = (stages || []).find((s) => (s.key || '').toString().toLowerCase() === stageStr);
    if (foundByKey) return foundByKey.label;
    
    // Try to find by label (in case backend returns labels instead of keys)
    const foundByLabel = (stages || []).find((s) => (s.label || '').toString().toLowerCase() === stageStr);
    if (foundByLabel) return foundByLabel.label;
    
    // If it looks like an ObjectId or long hex string, return Unknown
    if (stageStr.length > 20 || /^[a-f0-9]{20,}/.test(stageStr)) {
      return 'Unknown';
    }
    
    // Otherwise format the stage string nicely
    return stageStr.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const matchesMyDeals = (deal) => {
    if (!onlyMyDeals) return true;
    if (!user?.id) return false;

    const userId = String(user.id);
    const isOwner = String(deal?.created_by || "") === userId;

    const collabIds = Array.isArray(deal?.collab_ids)
      ? deal.collab_ids.map((id) => String(id))
      : [];

    const hasLegacySingleCollab = String(deal?.collab_id || "") === userId;
    const isCollaborator = collabIds.includes(userId) || hasLegacySingleCollab;

    return isOwner || isCollaborator;
  };

  // ---- Stats ----
  const totalDeals = deals.length;
  const totalValue = deals.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
  const activeDeals = deals.filter(d => !["closed_won", "closed_lost"].includes(d.stage)).length;


  // ---- Filter deals ----
  let filteredDeals = deals
    .filter(deal => (deal.title || "").toLowerCase().includes(debouncedSearch.toLowerCase()))
    .filter(filterByStage)
    .filter(matchesCompany)
    .filter(matchesAuthor)
    .filter(matchesMyDeals)
    .filter(deal => inDateRange(deal.created_at, createdFrom, createdTo))
    .filter(deal => inDateRange(deal.close_date, closeFrom, closeTo))
    .filter(deal => inValueRange(deal.amount, minValue, maxValue));

  // ---- Sorting ----
  if (sortConfig.field === 'title') {
    filteredDeals = filteredDeals.slice().sort((a, b) => {
      const nameA = (a.title || '').toLowerCase();
      const nameB = (b.title || '').toLowerCase();
      if (nameA < nameB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (nameA > nameB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  } else if (sortConfig.field === 'company_name') {
    filteredDeals = filteredDeals.slice().sort((a, b) => {
      const compA = (a.company_name || '').toLowerCase();
      const compB = (b.company_name || '').toLowerCase();
      if (compA < compB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (compA > compB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  } else if (sortConfig.field === 'amount') {
    filteredDeals = filteredDeals.slice().sort((a, b) => {
      const valA = parseFloat(a.amount || 0);
      const valB = parseFloat(b.amount || 0);
      return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
    });
  } else if (sortConfig.field === 'stage') {
    filteredDeals = filteredDeals.slice().sort((a, b) => {
      const stageA = (a.stage || '').toLowerCase();
      const stageB = (b.stage || '').toLowerCase();
      if (stageA < stageB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (stageA > stageB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  } else if (sortConfig.field === 'close_date') {
    filteredDeals = filteredDeals.slice().sort((a, b) => {
      const dateA = new Date(a.close_date);
      const dateB = new Date(b.close_date);
      return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
    });
  } else if (sortConfig.field === 'created_at') {
    filteredDeals = filteredDeals.slice().sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }

  // ---- Pagination ----
  const totalPages = Math.ceil(filteredDeals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDeals = filteredDeals.slice(startIndex, endIndex);

  // Reset to page 1 when filters change (but not on initial mount, so a
  // restored page number from a previous visit isn't immediately discarded).
  useEffect(() => {
    if (isInitialFilterRender.current) {
      isInitialFilterRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearch, activeStage, companyFilter, authorFilter, onlyMyDeals, createdFrom, createdTo, closeFrom, closeTo, minValue, maxValue]);

  // Persist filter/sort/pagination state so navigating to a deal (and back)
  // keeps the previous filtered view instead of resetting to defaults.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        DEALS_FILTERS_STORAGE_KEY,
        JSON.stringify({
          search,
          activeStage,
          companyFilter,
          authorFilter,
          createdFrom,
          createdTo,
          closeFrom,
          closeTo,
          minValue,
          maxValue,
          showAdvanced,
          onlyMyDeals,
          currentPage,
          sortConfig,
        })
      );
    } catch {
      // Ignore storage errors (e.g. private browsing quota exceeded)
    }
  }, [search, activeStage, companyFilter, authorFilter, createdFrom, createdTo, closeFrom, closeTo, minValue, maxValue, showAdvanced, onlyMyDeals, currentPage, sortConfig]);

  return (
    <div className="p-6 w-full">

      {/* PAGE TITLE + CTA */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <UiText variant="title" as="h1">Deals</UiText>
          <UiText variant="subtitle">Track and manage your sales opportunities</UiText>
        </div>

        <div className="flex gap-3">
          {user?.role !== 'user' && (
            <>
              <button onClick={() => navigate('/pipeline')} className="px-4 py-2 border rounded-lg flex items-center gap-2">
                <FaBullseye /> View Pipeline
              </button>

              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2">
                <FaPlus style={{ fontSize: "12px", verticalAlign: "middle" }} /> Add Deal
              </button>
            </>
          )}

        </div>
      </div>

      {/* STATS CARDS */}
      <div className="grid grid-cols-3 gap-6 mb-8">

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <FaBullseye className="text-blue-600" />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Total Deals</p>
              <p className="text-2xl font-bold text-gray-900">{totalDeals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-green-100 p-3 rounded-lg">
              <FaDollarSign className="text-green-600" />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">${totalValue.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-orange-100 p-3 rounded-lg">
              <FaBullseye className="text-orange-600" />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Active Deals</p>
              <p className="text-2xl font-bold text-gray-900">{activeDeals}</p>
            </div>
          </div>
        </div>

      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm mb-8">
        <div className="flex flex-col gap-4">

          {/* ROW 1: Search + Stage + Company */}
          <div className="flex flex-wrap gap-4 items-stretch">
            <div className="flex items-center flex-1 min-w-[260px]">
              <input
                type="text"
                placeholder="Search deals..."
                className="flex-1 h-11 px-4 border border-gray-300 rounded-xl bg-white cursor-text text-gray-700 shadow-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              value={activeStage}
              onChange={(e) => setActiveStage(e.target.value)}
              className="h-11 min-w-[170px] px-4 border border-gray-300 rounded-xl bg-white cursor-pointer text-gray-700 shadow-sm shrink-0"
              title="Stage"
            >
              <option value="all">All Stages</option>
              {(stages || []).map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>

            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="h-11 min-w-[170px] px-4 border border-gray-300 rounded-xl bg-white cursor-pointer text-gray-700 shadow-sm shrink-0"
              title="Company"
            >
              <option value="all">All Companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>


            {canViewAuthorFilter && (
              <select
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
                className="h-11 min-w-[190px] px-4 border border-gray-300 rounded-xl bg-white cursor-pointer text-gray-700 shadow-sm shrink-0"
                title="Author"
              >
                <option value="all">All Authors</option>
                {authorOptions.map((author) => (
                  <option key={author.id} value={author.id}>{author.name}</option>
                ))}
              </select>
            )}


            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="h-11 px-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 text-sm flex items-center gap-2 shrink-0"
            >
              <FaFilter />
              <span className="whitespace-nowrap">{showAdvanced ? "Hide filters" : "More filters"}</span>
              {advancedCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">{advancedCount}</span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="h-11 px-3 text-gray-500 hover:text-gray-700 text-sm underline shrink-0"
              >
                Clear all filters
              </button>
            )}

          </div>

          {/* ROW 2: Date filters */}
          <div className={`transition-all duration-200 ${showAdvanced ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <UiText variant="section">Advanced filters</UiText>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={clearAdvancedFilters} className="text-sm text-gray-500 hover:text-gray-700">Clear</button>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <div>
                  <UiText variant="label">Created From</UiText>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-sm text-gray-700"
                    value={createdFrom}
                    onChange={(e) => setCreatedFrom(e.target.value)}
                  />
                </div>

                <div>
                  <UiText variant="label">Created To</UiText>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-sm text-gray-700"
                    value={createdTo}
                    onChange={(e) => setCreatedTo(e.target.value)}
                  />
                </div>

                <div>
                  <UiText variant="label">Close From</UiText>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-sm text-gray-700"
                    value={closeFrom}
                    onChange={(e) => setCloseFrom(e.target.value)}
                  />
                </div>

                <div>
                  <UiText variant="label">Close To</UiText>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-sm text-gray-700"
                    value={closeTo}
                    onChange={(e) => setCloseTo(e.target.value)}
                  />
                </div>

                <div>
                  <UiText variant="label">Min Value</UiText>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-sm text-gray-700"
                    value={minValue}
                    onChange={(e) => setMinValue(e.target.value)}
                    placeholder="0"
                    min="0"
                  />
                </div>

                <div>
                  <UiText variant="label">Max Value</UiText>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-sm text-gray-700"
                    value={maxValue}
                    onChange={(e) => setMaxValue(e.target.value)}
                    placeholder="100000"
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* advanced filters now include Min/Max value inputs */}

        </div>
      </div>

      {/* TABLE + AI SIDEBAR */}
      <div className="bg-white rounded-xl shadow-sm p-6 w-full h-full flex-1 min-h-0">
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={() => setOnlyMyDeals((v) => !v)}
              className={`px-3 py-2 rounded-xl text-sm font-medium border ${onlyMyDeals ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
              title="Show deals where I am owner or collaborator"
            >
              My Deals
            </button>
          </div>

          <table className="w-full text-left">
            <thead>
              <tr className="border-b-2 border-gray-200">
                {/* Name */}
                <th className="pb-3 pt-2 cursor-pointer" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <UiText variant="section" as="span" style={{ color: '#374151' }}>Name</UiText>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                      onClick={() => {
                        setSortConfig((prev) => ({
                          field: 'title',
                          direction: prev.field === 'title' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }));
                      }}
                      title="Sort by Name"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          transform: sortConfig.field === 'title' && sortConfig.direction === 'asc' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                          color: sortConfig.field === 'title' ? '#2563eb' : '#9ca3af',
                        }}
                      >
                        <path d="M6 8.5L2 4.5H10L6 8.5Z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                </th>

                {/* Company */}
                <th className="pb-3 pt-2 cursor-pointer" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <UiText variant="section" as="span" style={{ color: '#374151' }}>Company</UiText>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                      onClick={() => {
                        setSortConfig((prev) => ({
                          field: 'company_name',
                          direction: prev.field === 'company_name' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }));
                      }}
                      title="Sort by Company"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          transform: sortConfig.field === 'company_name' && sortConfig.direction === 'asc' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                          color: sortConfig.field === 'company_name' ? '#2563eb' : '#9ca3af',
                        }}
                      >
                        <path d="M6 8.5L2 4.5H10L6 8.5Z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                </th>

                {/* Value */}
                <th className="pb-3 pt-2 cursor-pointer" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <UiText variant="section" as="span" style={{ color: '#374151' }}>Value</UiText>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                      onClick={() => {
                        setSortConfig((prev) => ({
                          field: 'amount',
                          direction: prev.field === 'amount' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }));
                      }}
                      title="Sort by Value"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          transform: sortConfig.field === 'amount' && sortConfig.direction === 'asc' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                          color: sortConfig.field === 'amount' ? '#2563eb' : '#9ca3af',
                        }}
                      >
                        <path d="M6 8.5L2 4.5H10L6 8.5Z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                </th>

                {/* Stage */}
                <th className="pb-3 pt-2 cursor-pointer" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <UiText variant="section" as="span" style={{ color: '#374151' }}>Stage</UiText>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                      onClick={() => {
                        setSortConfig((prev) => ({
                          field: 'stage',
                          direction: prev.field === 'stage' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }));
                      }}
                      title="Sort by Stage"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          transform: sortConfig.field === 'stage' && sortConfig.direction === 'asc' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                          color: sortConfig.field === 'stage' ? '#2563eb' : '#9ca3af',
                        }}
                      >
                        <path d="M6 8.5L2 4.5H10L6 8.5Z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                </th>

                {/* Created On */}
                <th className="pb-3 pt-2 cursor-pointer" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <UiText variant="section" as="span" style={{ color: '#374151' }}>Created On</UiText>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                      onClick={() => {
                        setSortConfig((prev) => ({
                          field: 'created_at',
                          direction: prev.field === 'created_at' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }));
                      }}
                      title="Sort by Created On"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          transform: sortConfig.field === 'created_at' && sortConfig.direction === 'asc' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                          color: sortConfig.field === 'created_at' ? '#2563eb' : '#9ca3af',
                        }}
                      >
                        <path d="M6 8.5L2 4.5H10L6 8.5Z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                </th>

                {/* Close Date */}
                <th className="pb-3 pt-2 cursor-pointer" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <UiText variant="section" as="span" style={{ color: '#374151' }}>Close Date</UiText>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                      onClick={() => {
                        setSortConfig((prev) => ({
                          field: 'close_date',
                          direction: prev.field === 'close_date' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }));
                      }}
                      title="Sort by Close Date"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          transform: sortConfig.field === 'close_date' && sortConfig.direction === 'asc' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                          color: sortConfig.field === 'close_date' ? '#2563eb' : '#9ca3af',
                        }}
                      >
                        <path d="M6 8.5L2 4.5H10L6 8.5Z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ padding: "2rem", textAlign: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
                      <Loader2 size={32} color="#3b82f6" style={{ animation: "spin 1s linear infinite" }} />
                      <span style={{ color: "#64748b", fontSize: "0.95rem" }}>Loading deals...</span>
                    </div>
                    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                  </td>
                </tr>
              ) : error ? (
                <tr><td colSpan="6">{error}</td></tr>
              ) : filteredDeals.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: "2rem", textAlign: "center", color: "#9ca3af" }}>
                    {user?.role === 'user' ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", color: "#9ca3af" }}>
                        <div style={{ fontSize: "1rem", fontWeight: 600 }}>You have not been collabrated yet</div>
                        <div style={{ fontSize: "0.95rem" }}>Kindly contact Administrator</div>
                      </div>
                    ) : hasActiveFilters ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", color: "#9ca3af" }}>
                        <div style={{ fontSize: "1rem", fontWeight: 600 }}>No deals match your filters</div>
                        <button
                          type="button"
                          onClick={clearAllFilters}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          Clear all filters
                        </button>
                      </div>
                    ) : (
                      <div>No deals found.</div>
                    )}
                  </td>
                </tr>
              ) : (
                paginatedDeals.map(deal => (
                    <tr 
                      key={deal.id} 
                      onClick={() => navigate(`/deal-details/${deal.id}`)}
                      className="border-t cursor-pointer transition-all hover:bg-gray-50"
                    >
                      <td className="py-4">
                        <UiText variant="section" as="span" className="font-semibold text-gray-900">{deal.title}</UiText>
                      </td>
                      <td className="py-4">
                        <UiText variant="small" as="span">{deal.company_name}</UiText>
                      </td>
                      <td className="py-4">
                        <UiText variant="small" as="span">${parseFloat(deal.amount).toLocaleString()}</UiText>
                      </td>
                      <td className="py-4">
                        <UiText variant="small" as="span">{stageLabel(deal.stage)}</UiText>
                      </td>
                      <td className="py-4">
                        <UiText variant="small" as="span">{formatDate(deal.created_at)}</UiText>
                      </td>
                      <td className="py-4">
                        <UiText variant="small" as="span">{formatDate(deal.close_date)}</UiText>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>

          {/* PAGINATION CONTROLS */}
          {filteredDeals.length > 0 && (
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "1.5rem",
              paddingTop: "1rem",
              borderTop: "1px solid #e5e7eb",
            }}>
              {/* Left: Items per page */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <UiText variant="small" as="span">Show:</UiText>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "0.4rem 0.6rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "13px",
                    cursor: "pointer",
                    background: "#fff",
                  }}
                >
                  <option value={6}>6</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <UiText variant="small" as="span" style={{ color: "#6b7280" }}>
                  of {filteredDeals.length} deals
                </UiText>
              </div>

              {/* Right: Page navigation */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: "0.4rem 0.8rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    background: currentPage === 1 ? "#f3f4f6" : "#fff",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: currentPage === 1 ? "#9ca3af" : "#374151",
                  }}
                >
                  Previous
                </button>

                <div style={{ display: "flex", gap: "0.25rem" }}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        style={{
                          padding: "0.4rem 0.7rem",
                          border: currentPage === pageNum ? "2px solid #2563eb" : "1px solid #d1d5db",
                          borderRadius: "6px",
                          background: currentPage === pageNum ? "#eff6ff" : "#fff",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: currentPage === pageNum ? "600" : "500",
                          color: currentPage === pageNum ? "#2563eb" : "#374151",
                          minWidth: "32px",
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: "0.4rem 0.8rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    background: currentPage === totalPages ? "#f3f4f6" : "#fff",
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: currentPage === totalPages ? "#9ca3af" : "#374151",
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      {showAddModal && (
        <AddDealModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchDeals(); // refresh table
          }}
        />
      )}

    </div>

  );
};

export default DealsPage;
